import { useLang } from "@/lib/i18n";
import { useReveal } from "@/lib/useReveal";

const IMAGES = [
  // Tetris arrangement: 6 cases
  {
    src: "https://images.unsplash.com/photo-1604074131228-9d48b811bd80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwd2Vic2l0ZSUyMGRlc2lnbiUyMG1vY2t1cHxlbnwwfHx8fDE3ODIyMDk3NTl8MA&ixlib=rb-4.1.0&q=85",
    aspect: "aspect-[4/5]",
  },
  {
    src: "https://images.unsplash.com/photo-1527443195645-1133f7f28990?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwd2Vic2l0ZSUyMGRlc2lnbiUyMG1vY2t1cHxlbnwwfHx8fDE3ODIyMDk3NTl8MA&ixlib=rb-4.1.0&q=85",
    aspect: "aspect-[4/3]",
  },
  {
    src: "https://images.pexels.com/photos/8408538/pexels-photo-8408538.jpeg",
    aspect: "aspect-[3/4]",
  },
  {
    src: "https://images.pexels.com/photos/36639285/pexels-photo-36639285.jpeg",
    aspect: "aspect-[4/5]",
  },
  {
    src: "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?crop=entropy&cs=srgb&fm=jpg",
    aspect: "aspect-[4/3]",
  },
  {
    src: "https://images.pexels.com/photos/4386356/pexels-photo-4386356.jpeg",
    aspect: "aspect-[3/4]",
  },
];

export const Gallery = () => {
  const { t } = useLang();
  const { ref, visible } = useReveal();
  return (
    <section
      id="work"
      data-testid="gallery-section"
      ref={ref}
      className={`py-20 md:py-32 bg-white border-y border-neutral-200 reveal-up ${visible ? "in-view" : ""}`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-y-8 lg:gap-x-12 mb-16 md:mb-24 items-end">
          <div className="col-span-12 lg:col-span-7">
            <div className="overline mb-6">{t.gallery.overline}</div>
            <h2 className="editorial-h text-4xl md:text-5xl lg:text-6xl whitespace-pre-line">
              {t.gallery.title}
            </h2>
          </div>
          <p className="col-span-12 lg:col-span-4 lg:col-start-9 text-[15px] md:text-base text-neutral-600 leading-relaxed">
            {t.gallery.subtitle}
          </p>
        </div>

        {/* Tetris grid */}
        <div className="grid grid-cols-6 gap-3 md:gap-5">
          {t.gallery.cases.map((c, i) => {
            const img = IMAGES[i % IMAGES.length];
            // Layout pattern for asymmetric editorial grid
            const layouts = [
              "col-span-6 md:col-span-3 lg:col-span-2",
              "col-span-6 md:col-span-3 lg:col-span-3 lg:row-span-1",
              "col-span-3 md:col-span-3 lg:col-span-1",
              "col-span-3 md:col-span-3 lg:col-span-2",
              "col-span-6 md:col-span-3 lg:col-span-2",
              "col-span-6 md:col-span-3 lg:col-span-2",
            ];
            return (
              <a
                key={i}
                href="#contact"
                data-testid={`gallery-item-${i}`}
                className={`group block ${layouts[i % layouts.length]}`}
              >
                <div className={`hover-zoom relative bg-neutral-100 ${img.aspect}`}>
                  <img
                    src={img.src}
                    alt={c.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="flex items-baseline justify-between mt-4">
                  <h3 className="font-serif text-xl md:text-2xl tracking-tight">
                    {c.name}
                  </h3>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                    {String(i + 1).padStart(2, "0")} / {String(t.gallery.cases.length).padStart(2, "0")}
                  </span>
                </div>
                <p className="text-xs md:text-sm text-neutral-500 mt-1">
                  {c.sector}
                </p>
              </a>
            );
          })}
        </div>

        <div className="mt-16 flex justify-center">
          <a
            href="#contact"
            data-testid="gallery-view-all"
            className="group inline-flex items-center gap-3 px-1 py-3 border-b border-black text-[12px] tracking-[0.15em] uppercase hover:gap-4 transition-all"
          >
            {t.gallery.viewAll}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};
